import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { Refresh as RenewIcon, Lock as LockIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useModal } from '../../contexts/ModalContext';
import { useLoader } from '../../contexts/LoaderContext';
import api from '../../services/api';

const RenewSubscriptionModal = ({ open, onClose, currentPlan, fetchSubscriptionData, onSuccess, onError }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openSuccessModal, openErrorModal } = useModal();
  const { showLoader, hideLoader } = useLoader();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    onClose();
    showLoader('Processing...');
    try {
      const response = await api.post('subscription/renew/', {
        request_type: 'RW',
        plan: currentPlan.plan?.id ?? currentPlan.plan_id,
        description: `Renewal request for ${currentPlan.plan?.name ?? currentPlan.plan_name} plan`
      });
      if (response.status === 200 || response.status === 201) {
        setTimeout(() => {
          hideLoader();
          // Call the onSuccess callback if provided
          if (onSuccess) {
            onSuccess();
          } else {
            // Fallback to default behavior
            openSuccessModal(
              'Request Submitted Successfully',
              'Subscription renewal request submitted successfully',
              () => {
                if (fetchSubscriptionData) fetchSubscriptionData();
              }
            );
          }
        }, 300);
      }
    } catch (error) {
      setTimeout(() => {
        hideLoader();
        // Call the onError callback if provided
        if (onError) {
          onError(error);
        } else {
          // Fallback to default behavior
          openErrorModal(
            'Request Failed',
            error.response?.data?.message || 'Failed to submit renewal request. Please try again or contact support.'
          );
        }
      }, 300);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
        <Box sx={{
          background: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '50%',
          width: 72,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}>
          <RenewIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          Renew Your Subscription
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
          This will extend your subscription.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{
          background: '#f5f7fa',
          borderRadius: 2,
          p: 2,
          mb: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center'
        }}>
          <Chip
            label={currentPlan?.plan?.name ?? currentPlan?.plan_name}
            color="primary"
            size="medium"
            sx={{ fontWeight: 600, fontSize: 16 }}
          />
          {(currentPlan?.plan?.end_date ?? currentPlan?.end_date) && (
            <Typography variant="body2" color="text.secondary">
              Expires: {new Date(currentPlan.plan?.end_date ?? currentPlan.end_date).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <InfoIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={600} textAlign="center">
            Are you sure you want to renew your <b>{currentPlan?.plan?.name ?? currentPlan?.plan_name}</b> subscription?
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          You can continue managing your leads and properties without interruption.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'center', gap: 2 }}>
        <Button
          color="error"
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 120, fontWeight: 600 }}
        >
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ minWidth: 180, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
          startIcon={<LockIcon />}
        >
          Renew Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewSubscriptionModal;
